import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';

const CustomSelect = ({ value, onChange, options, placeholder, icon: Icon, variant = 'outline' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                buttonRef.current && !buttonRef.current.contains(event.target) &&
                dropdownRef.current && !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Calculate dropdown position based on button position
    const handleOpen = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 8,
                left: rect.left,
                width: rect.width,
                zIndex: 9999,
            });
        }
        setIsOpen(prev => !prev);
    };

    const selectedOption = options.find(opt => opt.value === value);

    const getVariantStyles = () => {
        if (variant === 'flat') {
            return `border-none bg-transparent px-0 py-2 shadow-none focus-within:ring-0`;
        }
        return `border rounded-xl px-3 py-2.5 ${isOpen
            ? 'border-blue-500 ring-2 ring-blue-500/10 bg-white shadow-md'
            : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300 shadow-sm'
            }`;
    };

    const dropdown = isOpen && ReactDOM.createPortal(
        <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        >
            <div className="py-1 max-h-60 overflow-y-auto">
                {options.map((option) => {
                    const isSpecial = option.value === 'unknown_terminal';
                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                                onChange(option.value);
                                setIsOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors text-left
                                ${value === option.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'}
                                ${isSpecial ? 'border-t border-gray-50 mt-1' : ''}
                            `}
                        >
                            {isSpecial ? (
                                <div className="flex items-center gap-2 text-blue-600 italic">
                                    <Search className="w-4 h-4" />
                                    <span>{option.label}</span>
                                </div>
                            ) : (
                                <span>{option.label}</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>,
        document.body
    );

    return (
        <div className="relative w-full">
            <button
                ref={buttonRef}
                type="button"
                onClick={handleOpen}
                className={`w-full flex items-center justify-between transition-all duration-200 text-sm font-medium ${getVariantStyles()}`}
            >
                <div className="flex items-center gap-3 truncate">
                    {Icon && <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <span className={`truncate ${!value ? 'text-gray-400' : 'text-gray-700'}`}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
            </button>

            {dropdown}
        </div>
    );
};

export default CustomSelect;
